import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { BuddyTrip, BUDDIES_A } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import type { ScopedAssistantControl } from '@components/ScopedAssistantChat';
import type { AppFramePreview } from '../../appFrame/AppFrame';
import { useFullscreen } from '../../appFrame/useFullscreen';
import { useViewSnapshot } from '../../viewSnapshot';
import AppBuilderHeader from '../AppBuilderHeader';
import BuilderChat from '../BuilderChat';
import PublishButton from '../PublishButton';
import PublishReviewDialog from '../PublishReviewDialog';
import BuilderAgentCoach from './BuilderAgentCoach';
import BuilderCanvas from './BuilderCanvas';
import BuilderLayout from './BuilderLayout';
import BuilderNotices from './BuilderNotices';
import BuilderPaneTabs from './BuilderPaneTabs';
import { BUDDY_HOME_PREVIEW, BUDDY_HOME_THUMBNAIL, BUILDER_PANE_APP } from './builderWorkspace.constants';
import DetailsRail from './DetailsRail';
import { useAppThumbnail } from './useAppThumbnail';
import { useBuddyHandoff } from './useBuddyHandoff';
import { useBuildProgress, useDraftFollowsAgent } from './useBuildProgress';
import { coversTheApp, useBuildStage } from './useBuildStage';
import { useBuilderChatInbox } from './useBuilderChatInbox';
import { useBuilderLayout } from './useBuilderLayout';
import { useBuilderPublishing } from './useBuilderPublishing';
import { useBuilderRun } from './useBuilderRun';
import { usePreviewCapture } from './usePreviewCapture';
import { ChatBody, ChatColumn, ChatFooter, ChatFooterHint, WorkspaceRoot } from './BuilderWorkspace.styled';

interface BuilderWorkspaceProps {
  readonly draft: UserAppDraft;
  readonly onReload: () => void;
}

const BuilderWorkspace: React.FC<BuilderWorkspaceProps> = ({ draft, onReload }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { appKey } = draft;
  const inbox = useBuilderChatInbox();
  const layout = useBuilderLayout();
  const canvasHidden = layout.isPhone && layout.phonePane !== BUILDER_PANE_APP;
  const railOpen = layout.isPhone || layout.railOpen;
  const [problems, setProblems] = useState<readonly string[]>([]);
  const publishing = useBuilderPublishing(draft, onReload, setProblems);
  const { progress, status, onProgress } = useBuildProgress();
  useDraftFollowsAgent(progress.steps, onReload);
  const { controller, stale } = useBuilderRun(draft, setProblems);
  const chatControl = useRef<ScopedAssistantControl>(null);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const { capture } = useViewSnapshot({ surface: 'app_builder', appKey });
  const previewRef = useRef<AppFramePreview>(null);
  const onPreviewCapture = usePreviewCapture(appKey, previewRef, !canvasHidden);
  const [fullscreenRef, fullscreen] = useFullscreen<HTMLDivElement>();
  const hasPage = Boolean(draft.preview.presentation?.page);
  const thumbnail = useAppThumbnail(appKey);
  const [coaching, setCoaching] = useState(false);

  const { showChat, focusApp, chatVisible } = layout;
  const { stage, endSketch, endFirstSend } = useBuildStage({
    freshBuild: Boolean(inbox.initialSend),
    building: status.building,
    hasPage,
    onFirstBuildReady: useCallback(() => {
      focusApp();
      setCoaching(true);
    }, [focusApp]),
    onFirstBuildWithoutPage: showChat,
  });
  const covered = coversTheApp(stage);
  // The sketch already has both buddies guiding it, so they split only once it has been put away.
  const handoff = useBuddyHandoff(thumbnail.generating, stage === 'building' || stage === 'revealing');
  const endCoaching = useCallback(() => setCoaching(false), []);
  if (coaching && chatVisible) setCoaching(false);

  const { draftInChat, sendInChat: sendDraft } = inbox;
  const askInChat = useCallback(
    (text: string) => {
      draftInChat(text);
      showChat();
    },
    [draftInChat, showChat],
  );
  const sendInChat = useCallback(
    (text: string) => {
      sendDraft(text);
      showChat();
    },
    [sendDraft, showChat],
  );
  const askToFixReview = (findings: readonly string[]) => {
    publishing.closeReview();
    askInChat(
      intl.formatMessage(
        {
          id: 'apps.builder.review.fixRequest',
          defaultMessage: 'The publish review found these problems; fix them:\n{findings}',
        },
        { findings: findings.map((finding) => `- ${finding}`).join('\n') },
      ),
    );
  };
  return (
    <WorkspaceRoot data-testid="app-builder">
      <AppBuilderHeader
        draft={draft}
        versions={publishing.versions}
        restoreDisabled={assistantBusy || publishing.isPublishing || publishing.isRestoring}
        compact={layout.isPhone}
        isPublishing={publishing.isPublishing}
        isRestoring={publishing.isRestoring}
        showPublish={!chatVisible}
        fullscreen={hasPage ? fullscreen : undefined}
        onBack={() => navigate('/apps')}
        onRestore={publishing.restore}
        onPublish={publishing.publish}
      />
      {layout.isPhone && (
        <BuilderPaneTabs
          pane={layout.phonePane}
          onSelect={layout.setPhonePane}
          appApprovals={controller?.live.pendingApprovals.length ?? 0}
        />
      )}
      <BuilderLayout
        railOpen={railOpen}
        chatOpen={layout.chatOpen}
        phonePane={layout.phonePane}
        rail={
          <DetailsRail
            draft={draft}
            onReload={onReload}
            open={railOpen}
            onToggle={layout.isPhone ? undefined : layout.toggleRail}
            building={status.building}
            filling={covered}
            thumbnailBuddy={handoff.thumbnail}
          />
        }
        canvas={
          <BuilderCanvas
            draft={draft}
            controller={controller}
            stale={stale}
            hidden={canvasHidden}
            chatOpen={layout.chatOpen}
            onToggleChat={layout.isPhone ? undefined : layout.toggleChat}
            stage={stage}
            brief={inbox.initialSend?.text ?? null}
            onSketchDone={endSketch}
            build={status}
            steps={progress.steps}
            buddy={handoff.preview}
            onStop={() => {
              endSketch();
              endFirstSend();
              chatControl.current?.stop();
            }}
            notices={
              <BuilderNotices draft={draft} publishing={publishing} problems={problems} onAskAssistant={askInChat} />
            }
            coach={coaching && <BuilderAgentCoach anchored={!layout.isPhone} onDismiss={endCoaching} />}
            onFix={sendInChat}
            fullscreenRef={fullscreenRef}
            fullscreen={fullscreen}
            previewRef={previewRef}
          />
        }
        chat={
          <ChatColumn>
            <ChatBody>
              <BuilderChat
                key={appKey}
                appKey={appKey}
                onDraftEdited={onReload}
                onBusyChange={setAssistantBusy}
                snapshot={hasPage && !canvasHidden ? capture : undefined}
                onPreviewCapture={onPreviewCapture}
                draft={inbox.draft}
                initialSend={inbox.initialSend}
                onInitialSendSettled={endFirstSend}
                onProgress={onProgress}
                controlRef={chatControl}
              />
            </ChatBody>
            {chatVisible && (
              <ChatFooter>
                {!hasPage && (
                  <ChatFooterHint>
                    {intl.formatMessage({
                      id: 'apps.builder.publish.hint',
                      defaultMessage: 'Ready to publish once the page is built.',
                    })}
                  </ChatFooterHint>
                )}
                <PublishButton
                  draft={draft}
                  isPublishing={publishing.isPublishing}
                  isRestoring={publishing.isRestoring}
                  onPublish={publishing.publish}
                />
              </ChatFooter>
            )}
          </ChatColumn>
        }
      />
      {handoff.running && (
        <BuddyTrip who={BUDDIES_A} from={BUDDY_HOME_THUMBNAIL} to={BUDDY_HOME_PREVIEW} onArrive={handoff.arrived} />
      )}
      <PublishReviewDialog
        review={publishing.review}
        applying={publishing.isSavingPrompt}
        publishing={publishing.isPublishing}
        onApply={publishing.applySuggestion}
        onPublishAnyway={publishing.publishAnyway}
        onAskAssistant={askToFixReview}
        onClose={publishing.closeReview}
      />
    </WorkspaceRoot>
  );
};

export default BuilderWorkspace;
